import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { clearAuthToken } from '../utils/authTokenStorage';
import getConfig from '../utils/configService';

const AuthContext = createContext(null);

// Statuses nginx returns while the backend is still starting (upstream down /
// timing out). The all-in-one serves the SPA before uvicorn is ready, so the
// first /auth/check can hit these — retry rather than dropping to the login
// screen. A live backend that answers any other status (or 200) is treated as
// a real, immediate answer.
const BACKEND_STARTING_STATUSES = [502, 503, 504];
// Cover the all-in-one cold start ("allow ~60 s on first run"): exponential
// backoff capped at 5 s over this many retries ≈ ~60 s before giving up.
const AUTH_CHECK_MAX_RETRIES = 14;
const AUTH_CHECK_BASE_DELAY_MS = 500;
const AUTH_CHECK_MAX_DELAY_MS = 5000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Pending retry timer, cleared on unmount so it can't setState afterwards.
  const retryTimerRef = useRef(null);

  const API_URL = getConfig().BACKEND_API;

  // `attempt` recurses via setTimeout on a transient failure. `loading` stays
  // true across retries so the UI shows "Loading…" instead of flashing the
  // login page while the backend boots (issue: Helmholtz login flicker on the
  // all-in-one).
  const checkAuth = useCallback(async (attempt = 0) => {
    try {
      const response = await fetch(`${API_URL}/auth/check`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          // Reconcile the client guest flag with the authoritative backend
          // signal: a non-guest session must not keep a stale guest_mode
          // flag (it would misroute 401s to /handoff-expired).
          if (!data.user?.guest) sessionStorage.removeItem('guest_mode');
          setUser(data.user);
        } else {
          clearAuthToken();
          setUser(null);
        }
        setLoading(false);
        return;
      }

      // A live backend answered: a non-gateway status is a real "not
      // authenticated", not a startup race — don't retry.
      if (!BACKEND_STARTING_STATUSES.includes(response.status)) {
        clearAuthToken();
        setUser(null);
        setLoading(false);
        return;
      }
      // else: gateway status → backend still starting → fall through to retry
    } catch (error) {
      // Network error (backend not reachable yet) → fall through to retry.
      console.error('Auth check failed:', error);
    }

    if (attempt >= AUTH_CHECK_MAX_RETRIES) {
      // Backend never became reachable. Give up without clearing the token
      // (the failure may be transient) and let the route guard fall back.
      setUser(null);
      setLoading(false);
      return;
    }
    const delay = Math.min(AUTH_CHECK_BASE_DELAY_MS * 2 ** attempt, AUTH_CHECK_MAX_DELAY_MS);
    retryTimerRef.current = setTimeout(() => { checkAuth(attempt + 1); }, delay);
  }, [API_URL]);

  useEffect(() => {
    checkAuth();
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [checkAuth]);

  const login = () => {
    // Save the current page to redirect back after login
    localStorage.setItem('redirect_after_login', window.location.pathname);
    window.location.href = `${API_URL}/auth/login`;
  };

  const startTrial = async () => {
    const response = await fetch(`${API_URL}/auth/guest`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error(`Trial start failed with status ${response.status}`);
    }
    await checkAuth();
  };

  const logout = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Logout failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Logout error:', error);
      return;
    }
    
    clearAuthToken();
    localStorage.removeItem('redirect_after_login');
    sessionStorage.removeItem('guest_mode');
    setUser(null);
    window.location.href = '/';
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isGuest: !!user?.guest,
    // No-login trial guest (not a partner handoff); set by the backend.
    isTrialGuest: !!user?.trial,
    // Guest session expiry (unix seconds) when present, else null.
    sessionExp: user?.session_exp ?? null,
    login,
    startTrial,
    logout,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
